import React, { useEffect, useState } from "react";
import {
    Hourglass, Clock, AlertCircle, Calendar, ChevronDown, ChevronUp
} from "lucide-react";
import { API_BASE_URL } from "../config/apiConfig";
import { useNavigate } from "react-router-dom";


const CountdownTimer = () => {
    const [nextExam, setNextExam] = useState(null);
    const [status, setStatus] = useState("loading");
    const [error, setError] = useState(null);
    const [allExams, setAllExams] = useState([]);
    const [expanded, setExpanded] = useState(false);
    const [examTimers, setExamTimers] = useState({});
    const navigate = useNavigate();
    const token = localStorage.getItem("token");
    console.log(nextExam)

    // 🧮 Fixed: Proper UTC time calculation
    const calculateExamStatus = (exam) => {
        const now = Date.now(); // UTC
        const start = new Date(exam.startTime).getTime(); // UTC
        const end = start + exam.availableDuration * 60 * 60 * 1000; // also UTC

        if (now < start) {
            return {
                status: "before",
                timeLeft: formatTimeLeft(start - now),
            };
        } else if (now >= start && now < end) {
            return {
                status: "open",
                timeLeft: formatTimeLeft(end - now),
            };
        } else {
            return { status: "closed", timeLeft: null };
        }
    };



    const formatTimeLeft = (diff) => {
        if (diff <= 0) return null;

        const seconds = Math.floor(diff / 1000);
        const minutes = Math.floor(seconds / 60);
        const hours = Math.floor(minutes / 60);
        const days = Math.floor(hours / 24);

        return {
            days: days,
            hours: hours % 24,
            minutes: minutes % 60,
            seconds: seconds % 60,
        };
    };

    // ⏱ Fetch upcoming exams
    useEffect(() => {
        const fetchUpcomingExams = async () => {
            try {
                setStatus("loading");
                setError(null);

                const res = await fetch(`${API_BASE_URL}/api/tests/upcoming-all`, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                });

                const data = await res.json();
                console.log(data)
                if (!res.ok) throw new Error(data.message || "Failed to fetch upcoming exams");

                if (data.exams && data.exams.length > 0) {
                    const sorted = data.exams.sort(
                        (a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
                    );
                    setAllExams(sorted);
                    setNextExam(sorted[0]);
                } else {
                    setNextExam(null);
                    setStatus("none");
                }
            } catch (err) {
                console.error("Error fetching upcoming exams:", err.message);
                setError(err.message);
                setStatus("error");
            }
        };

        fetchUpcomingExams();
    }, [token]);

    // ⏳ Update all timers every second
    useEffect(() => {
        if (allExams.length === 0) return;

        const updateAllExamTimers = () => {
            const updated = {};
            allExams.forEach((exam) => {
                updated[exam.exam_id] = calculateExamStatus(exam);
            });
            setExamTimers(updated);

            // Also update global "next exam" status
            const next = allExams[0];
            const nextInfo = calculateExamStatus(next);
            setStatus(nextInfo.status);
        };

        updateAllExamTimers();
        const timer = setInterval(updateAllExamTimers, 1000);
        return () => clearInterval(timer);
    }, [allExams]);

    const handleNavigate = (exam) => {
        const examState = examTimers[exam.exam_id]?.status;
        if (examState === "open") {
            navigate(`/take-exam?subject=${encodeURIComponent(exam.subject)}`);
        }
    };

    // 🧭 UI States
    if (status === "loading") {
        return (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-full shadow-sm">
                <Clock className="w-3.5 h-3.5 text-blue-600 animate-spin" />
                <span className="text-xs font-medium text-blue-700">Loading...</span>
            </div>
        );
    }

    if (status === "error") {
        return (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-red-50 border border-red-200 rounded-full shadow-sm">
                <AlertCircle className="w-3.5 h-3.5 text-red-600" />
                <span className="text-xs font-medium text-red-700">Error loading</span>
            </div>
        );
    }

    if (!nextExam) {
        return (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-full shadow-sm">
                <Calendar className="w-3.5 h-3.5 text-gray-500" />
                <span className="text-xs font-medium text-gray-600">No upcoming exams</span>
            </div>
        );
    }

    const nextExamTimer = examTimers[nextExam.exam_id];
    const timeLeft = nextExamTimer?.timeLeft;
    const nextStatus = nextExamTimer?.status;

    const totalHours = (timeLeft?.days || 0) * 24 + (timeLeft?.hours || 0);
    const isUrgent = totalHours < 2;
    const isWarning = totalHours < 6;

    return (
        <div className="w-full max-w-md">
            {/* Compact Countdown */}
            <div
                onClick={() => handleNavigate(nextExam)}
                className={`cursor-pointer flex items-center justify-between px-3 py-1.5 rounded-full shadow-sm border transition-all duration-300 hover:shadow-md hover:scale-[1.02]
        ${nextStatus === "before"
                        ? "bg-gradient-to-r from-blue-50 to-cyan-50 border-blue-300"
                        : isUrgent
                            ? "bg-gradient-to-r from-red-50 to-orange-50 border-red-300"
                            : isWarning
                                ? "bg-gradient-to-r from-yellow-50 to-amber-50 border-yellow-300"
                                : "bg-gradient-to-r from-green-50 to-emerald-50 border-green-200"
                    }`}
            >
                {/* exam.exam_id */}
                <div className="flex items-center gap-2">
                    <Hourglass
                        className={`w-3.5 h-3.5 ${nextStatus === "before"
                            ? "text-blue-700"
                            : isUrgent
                                ? "text-red-700 animate-pulse"
                                : isWarning
                                    ? "text-yellow-700"
                                    : "text-green-700"
                            }`}
                    />
                    <div className="flex items-center gap-1.5">
                        <div className="flex items-center gap-1">
                            <span
                                className={`text-xs font-semibold ${nextStatus === "before"
                                    ? "text-blue-800"
                                    : isUrgent
                                        ? "text-red-800"
                                        : isWarning
                                            ? "text-yellow-800"
                                            : "text-green-800"
                                    }`}
                            >
                                {nextExam.subject}
                            </span>
                            {nextExam.started && (
                                <span className="text-[10px] text-green-700 bg-green-100 border border-green-300 rounded-full px-1.5 py-0.5 flex items-center gap-0.5 font-medium">
                                    ✓ Written
                                </span>
                            )}
                        </div>
                        <span
                            className={`text-xs font-semibold ${nextStatus === "before"
                                ? "text-blue-800"
                                : isUrgent
                                    ? "text-red-800"
                                    : isWarning
                                        ? "text-yellow-800"
                                        : "text-green-800"
                                }`}
                        >
                            {nextStatus === "before" ? "starts in:" : nextStatus === "open" ? "open for:" : "closed"}
                        </span>
                        {timeLeft && (
                            <div className="flex items-center gap-0.5 font-mono text-xs font-bold">
                                {timeLeft.days > 0 && (
                                    <>
                                        <span>{timeLeft.days}d</span>
                                        <span className="text-gray-400">:</span>
                                    </>
                                )}
                                <span>{timeLeft.hours.toString().padStart(2, "0")}h</span>
                                <span className="text-gray-400">:</span>
                                <span>{timeLeft.minutes.toString().padStart(2, "0")}m</span>
                                <span className="text-gray-400">:</span>
                                <span>{timeLeft.seconds.toString().padStart(2, "0")}s</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Toggle Button */}
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        setExpanded(!expanded);
                    }}
                    className="p-1 rounded-full hover:bg-white/50 transition"
                >
                    {expanded ? (
                        <ChevronUp className="w-4 h-4 text-gray-600" />
                    ) : (
                        <ChevronDown className="w-4 h-4 text-gray-600" />
                    )}
                </button>
            </div>

            {/* Expandable Section */}
            {expanded && (
                <div className="mt-2 bg-white border border-gray-200 rounded-lg shadow-sm p-2 animate-fadeIn">
                    {allExams.map((exam) => {
                        const info = examTimers[exam.exam_id];
                        const timer = info?.timeLeft;
                        const st = info?.status;

                        return (
                            <div
                                key={exam.exam_id}
                                onClick={() => handleNavigate(exam)}
                                className={`flex justify-between items-center px-2 py-1 text-sm border-b last:border-none cursor-pointer rounded-md transition
                  ${st === "open" ? "hover:bg-green-50" : "hover:bg-gray-50"}
                `}
                            >
                                <div className="flex flex-col">
                                    <span className="font-medium text-gray-800 flex items-center gap-1">
                                        {exam.subject}
                                        {exam.started && (
                                            <span className="text-xs text-green-700 bg-green-100 border border-green-300 rounded-full px-1.5 py-0.5 flex items-center gap-1">
                                                ✓ Written
                                            </span>
                                        )}
                                    </span>
                                    <span className="text-xs text-gray-500">
                                        {new Date(exam.startTime).toLocaleString()}
                                    </span>
                                </div>


                                <div className="flex flex-col items-end">
                                    <span
                                        className={`text-xs font-semibold ${st === "before"
                                            ? "text-blue-700"
                                            : st === "open"
                                                ? "text-green-700"
                                                : "text-gray-500"
                                            }`}
                                    >
                                        {st === "before"
                                            ? "starts in"
                                            : st === "open"
                                                ? "open for"
                                                : "closed"}
                                    </span>

                                    {timer ? (
                                        <span className="font-mono text-[11px]">
                                            {timer.days > 0 && `${timer.days}d `}
                                            {timer.hours.toString().padStart(2, "0")}h{" "}
                                            {timer.minutes.toString().padStart(2, "0")}m{" "}
                                            {timer.seconds.toString().padStart(2, "0")}s
                                        </span>
                                    ) : (
                                        <span className="text-[11px] text-gray-400">--</span>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default CountdownTimer;